/**
 * Conflict Resolution Dialog
 *
 * Modal dialog shown when an optimistic locking conflict (HTTP 409) occurs.
 * Displays a comparison of the user's values vs. the server's current values
 * for changed fields, and allows the user to keep their changes or accept
 * the server's version.
 */

import React from "react";
import {
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  Sheet,
} from "@mui/joy";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";

interface ConflictResolutionDialogProps {
  open: boolean;
  onClose: () => void;
  onKeepMine: (data: Record<string, unknown>) => void;
  onUseTheirs: () => void;
  yourValues: Record<string, unknown>;
  serverValues: Record<string, unknown>;
  columns: Array<{ field: string; headerName: string }>;
}

/**
 * Format a cell value for display in the comparison table.
 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "(empty)";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export default function ConflictResolutionDialog({
  open,
  onClose,
  onKeepMine,
  onUseTheirs,
  yourValues,
  serverValues,
  columns,
}: ConflictResolutionDialogProps) {
  // Find fields where your values differ from the server values
  const changedFields = columns.filter((col) => {
    const yours = yourValues[col.field];
    const theirs = serverValues[col.field];
    return yours !== theirs;
  });

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        variant="outlined"
        role="alertdialog"
        sx={{ maxWidth: 600, overflow: "auto" }}
      >
        <DialogTitle>
          <WarningRoundedIcon />
          Edit Conflict
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            This row was modified by another user since you last loaded it.
            Please review the differences below and choose how to proceed.
          </Typography>

          {changedFields.length > 0 ? (
            <Sheet
              variant="outlined"
              sx={{ borderRadius: "sm", overflow: "auto" }}
            >
              <Table
                size="sm"
                stripe="odd"
                sx={{ "& th": { fontWeight: "lg" } }}
              >
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Your Value</th>
                    <th>Server Value</th>
                  </tr>
                </thead>
                <tbody>
                  {changedFields.map((col) => (
                    <tr key={col.field}>
                      <td>
                        <Typography level="body-sm" fontWeight="md">
                          {col.headerName}
                        </Typography>
                      </td>
                      <td>
                        <Typography level="body-sm">
                          {formatValue(yourValues[col.field])}
                        </Typography>
                      </td>
                      <td>
                        <Typography level="body-sm">
                          {formatValue(serverValues[col.field])}
                        </Typography>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Sheet>
          ) : (
            <Typography level="body-sm" sx={{ fontStyle: "italic" }}>
              No visible field differences detected. The conflict may involve
              internal metadata.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="solid"
            color="warning"
            onClick={() => onKeepMine(yourValues)}
          >
            Keep Mine
          </Button>
          <Button variant="solid" color="neutral" onClick={onUseTheirs}>
            Use Theirs
          </Button>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
